import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject } from '@angular/core';
import { Profile, ProfileDTO } from '../../../../core/models/users.interface';
import { FormBuilder, Validators } from '@angular/forms';
import { debounceTime, Subject, switchMap, takeUntil } from 'rxjs';
import { ProfileService } from '../../../../core/services/profile/profile';
import { Title } from '@angular/platform-browser';
import { RequestService } from '../../../../core/services/request/request';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { PAGINATION } from '../../../../core/constants/constants';
import { Filter } from '../../../../core/types/query-types';
import { FormUtils } from '../../../../shared/utils';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoadingService } from '../../../../core/services/loading/loading';
import { TermPipe } from '../../../../shared/pipes/term.pipe';
import { TermKey } from '../../../../core/models/terminology.interface';

@Component({
  selector: 'app-profile-form',
  imports: [
    MatFormField,
    ReactiveFormsModule,
    MatLabel,
    MatInput,
    MatError,
    MatIcon,
    MatIconModule,
    FormsModule,
    TermPipe,
  ],
  templateUrl: './profile-form.html',
  styleUrl: './profile-form.css',
})
export class ProfileForm {
  profile: Profile | null = null;
  profileId: number | null = null;
  errorMsg: string[] | null = null;
  previousExist = false;
  termKey = TermKey;
  protected _onDestroy = new Subject<void>();
  private formBuilder = inject(FormBuilder);

  constructor(
    private profileService: ProfileService,
    private title: Title,
    private requestService: RequestService,
    private toastr: ToastrService,
    private router: Router,
    private loadingService: LoadingService,
    private dialogRef: MatDialogRef<ProfileForm>,
  ) {
    this.title.setTitle('Crear/editar perfil');
  }

  private trackFields(): void {
    this.profileForm
      .get('username')
      ?.valueChanges.pipe(
        takeUntil(this._onDestroy),
        debounceTime(PAGINATION.delay_time),
        switchMap((username) => {
          if (username && username.toString().length > 5 && !this.profile) {
            const filters: Filter[] = [{ name: 'is_active', value: 0 }];
            const query = this.requestService.genDefaultParams(filters, username);
            return this.profileService.allProfiles(query);
          }
          return [];
        }),
      )
      .subscribe((profiles) => {
        if (profiles.count === 1) {
          this.previousExist = true;
          this.profile = profiles.results[0];
          this.profileForm.patchValue({ ...this.profile });
          this.profileForm.disable();
        }
      });
  }

  ngOnInit(): void {
    this.trackFields();
    this.profileService.asyncErrorValidation$.subscribe((error) => {
      if (error) {
        this.errorMsg = FormUtils.parseErrorMsgs(error);
      }
    });
    this.profileService.profileSelected$.subscribe((profile) => {
      if (profile) {
        this.profile = profile;
        this.profileId = profile.id;
        this.profileForm.patchValue({ ...profile });
      }
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  profileForm = this.formBuilder.group({
    username: ['', [Validators.required, Validators.minLength(5)]],
    first_name: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(150)]],
    last_name: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(150)]],
    email: [
      '',
      [
        Validators.required,
        Validators.pattern(
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        ),
      ],
    ],
  });

  saveProfile() {
    const payload: ProfileDTO = {
      first_name: this.profileForm.get('first_name')?.value ?? '',
      last_name: this.profileForm.get('last_name')?.value ?? '',
      username: this.profileForm.get('username')?.value ?? '',
      email: this.profileForm.get('email')?.value ?? '',
    };

    //activar perfil/usuario
    if (this.profile && this.previousExist) {
      this.profileService.activateProfile(this.profile.id).subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.router.navigate(['/profile-list']);
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }

    //actualizar perfil/usuario
    if (
      this.profile &&
      this.profileId !== null &&
      this.profileForm.valid &&
      this.profileForm.touched &&
      this.profileForm.dirty &&
      !this.previousExist
    ) {
      this.loadingService.show(true);
      this.profileService.updateProfile(payload, this.profileId.toString()).subscribe({
        next: (profile) => {
          this.loadingService.show(false);
          this.dialogRef.close();
          const msg = `El perfil ${profile.username} fue EDITADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }

    //crear usuario
    if (!this.profile && this.profileForm.status === 'VALID') {
      this.loadingService.show(true);
      this.profileService.createProfile(payload).subscribe({
        next: (profile) => {
          this.loadingService.show(false);
          this.dialogRef.close();
          const msg = `El perfil ${profile.username} fue CREADO correctamente!`;
          this.toastr.success(msg, 'Exitoso!', {
            progressBar: true,
            timeOut: 5000,
          });
        },
        error: ({}: HttpErrorResponse) => {
          if (this.errorMsg) {
            this.loadingService.show(false);
            this.toastr.error('Error: ' + this.errorMsg, undefined, {
              progressBar: true,
              timeOut: 5000,
            });
          }
        },
      });
    }
  }

  onActivateProfile() {
    if (this.profile && this.previousExist) {
      this.profileService
        .activateProfile(this.profile.id)
        .subscribe(this.requestService.resHandler(this.profile.username, 'perfiles', 'activado'));
    }
  }
}
